import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import {AxiosError} from "axios";
import {IApiResponse} from "data/services/http";
import {extractErrorMessage} from "data/utils";
import {ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IContestsStore} from "data/stores/contests/contests.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IUserStore} from "data/stores/user/user.store";
import {useNavigate} from "react-router-dom";
import type {IContestPlayersStore} from "data/stores/contest_players/contest_players.store";
import {AnalyticService} from "data/utils/track_analytics";
import {getToken} from "data/utils/recaptcha";
import {ISavePredictionParams} from "data/providers/api/predictions.api.provider";
import {createFanHubEvent, EventGUID} from "data/utils/analytics";

interface IProps {
	gameFeedId: number;
	navigate: ReturnType<typeof useNavigate>;
}

export interface ISaveBarController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;

	get isLoading(): boolean;
	get isPredictionSaved(): boolean;
	get isReadyForUpdate(): boolean;
	get isSubmitDisabled(): boolean;
	get buttonCopy(): string;
	get noteCopy(): string;
	get isSelectedPlayerInactive(): boolean;

	onSubmitVote: () => void;
}

@injectable()
export class SaveBarController implements ISaveBarController {
	@observable private _navigate?: ReturnType<typeof useNavigate>;
	@observable private _gameFeedId: number = 0;

	get isLoading() {
		return this._predictionsStore.getIsLoading;
	}

	get isPredictionSaved() {
		return this._predictionsStore.isPredictionSaved;
	}

	get isReadyForUpdate() {
		return this._predictionsStore.isReadyForUpdate;
	}

	get isSubmitDisabled() {
		const playerId = this._predictionsStore.prediction.playerId;
		const selectedPlayer = this._contestPlayersStore.getPlayerById(playerId);

		if (selectedPlayer?.status === "inactive") {
			return true;
		}

		if (!playerId) {
			return true;
		}

		if (this.isReadyForUpdate) {
			return false;
		}

		return this.isPredictionSaved;
	}

	get noteCopy() {
		const selectedPlayer = this._contestPlayersStore.getPlayerById(
			this._predictionsStore.prediction.playerId
		);

		if (selectedPlayer && selectedPlayer?.status === "inactive") {
			return this.i18n.t(
				"save_bar.inactive_player",
				"Player no longer available. Choose another."
			);
		} else {
			return this.i18n.t("save_bar.update_copy", "This will override your current vote");
		}
	}

	get buttonCopy() {
		if (this.isReadyForUpdate) {
			return this.i18n.t("save_bar.button.update", "Update Vote");
		}
		if (this.isPredictionSaved) {
			return this.i18n.t("save_bar.button.submitted", "Vote Submitted");
		}

		return this.i18n.t("save_bar.button.submit", "Submit Vote");
	}

	get isSelectedPlayerInactive() {
		const selectedPlayer = this._contestPlayersStore.getPlayerById(
			this._predictionsStore.prediction.playerId
		);

		return selectedPlayer?.status === "inactive";
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) public _userStore: IUserStore,
		@inject(Bindings.PredictionsStore) public _predictionsStore: IPredictionsStore,
		@inject(Bindings.ModalsStore) public _modalStore: IModalsStore,
		@inject(Bindings.PlayersStore) public _playerStore: IPlayersStore,
		@inject(Bindings.ContestPlayersStore) public _contestPlayersStore: IContestPlayersStore,
		@inject(Bindings.ContestsStore) public _contestStore: IContestsStore,
		@inject(Bindings.SquadsStore) public _squadsStore: ISquadsStore
	) {
		makeAutoObservable(this);
	}

	onSubmitVote = () => {
		const currentContest = this._contestStore.getContestByFeedId(this._gameFeedId);
		const player = this._playerStore.getPlayerById(this._predictionsStore.prediction.playerId);
		const isUpdate = this.isReadyForUpdate;
		getToken()
			.then((token) => {
				if (isUpdate) {
					createFanHubEvent(EventGUID.PickUpdated);
				} else {
					createFanHubEvent(EventGUID.PickSubmit);
				}

				const params: ISavePredictionParams = {
					...this._predictionsStore.prediction,
					token,
				};

				if (!this._userStore.isAuthorized) {
					params.guestUid = this._userStore.guestUid;
				}

				return this._predictionsStore.savePrediction(params);
			})
			.then(this.showConfirmModal)
			.catch((err: AxiosError<IApiResponse>) => this.onError(err));

		AnalyticService.trackAction({
			"action.name": this._predictionsStore.isReadyForUpdate
				? "Player of the Game: Update Vote Click"
				: "Player of the Game: Submit Vote Click",
			"action.location": "Major League Baseball: Player of the Game: Vote",
			"action.category": "Player of the Game",
			"action.element.text": "Submit vote",
			"baseball.playerId": String(player?.feedId || 0),
			"baseball.gamePk": String(currentContest?.gameFeedId),
		});
		// }
	};

	private showConfirmModal = () => {
		this._modalStore.showModal(ModalType.CONFIRM, {
			message: "",
			playerId: this._predictionsStore.prediction.playerId,
			contestId: this._predictionsStore.prediction.contestId,
			callback: () => this._modalStore.hideModal(),
		});
	};

	private onError = (error: AxiosError<IApiResponse>) => {
		const errorMsg = extractErrorMessage(error);

		this._modalStore.showModal(ModalType.ERROR, {
			message: errorMsg,
		});
	};

	init({gameFeedId, navigate}: IProps) {
		this._gameFeedId = gameFeedId;
		this._navigate = navigate;
	}
}
